import { DateTime } from 'luxon';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { DateHelper } from '@getaccept/lib-shared/src/helpers/date.helper';
import type { EmojiLocalization } from '@getaccept/fried-tofu';
import type {
  ChatParticipant,
  ChatParticipantId,
  Conversation,
  Message,
  MessageDraft,
  OpenConversationPayload,
} from '../types';
import { ChatUnavailableReason, ConversationType } from '../types';
import type { Participant } from '../../types';
import AvatarHelper from '../../helpers/avatar.helper';

export default class ChatHelper {
  static getEmojiLocalization(): EmojiLocalization {
    return {
      choose: t('choose').toString(),
      search: t('find-your-emoji').toString(),
      notfound: t('no-emoji-found').toString(),
      searchresult: t('search-results').toString(),
      recent: t('frequently-used').toString(),
      smileys: t('smileys-and-emoticon').toString(),
      people: t('people-and-body').toString(),
      nature: t('animals-and-nature').toString(),
      foods: t('food-and-drink').toString(),
      activity: t('activity').toString(),
      places: t('travel-and-places').toString(),
      objects: t('objects').toString(),
      symbols: t('symbols').toString(),
      flags: t('flags').toString(),
      custom: t('custom').toString(),
    };
  }

  static getPreviousMessage(message: Message, messages: Message[]): Message {
    const index: number = messages.indexOf(message) - 1;
    return messages[index];
  }

  static getNextMessage(message: Message, messages: Message[]): Message {
    const index: number = messages.indexOf(message) + 1;
    return messages[index];
  }

  static getLastMessage(messages: Message[]) {
    if (!messages) {
      return;
    }
    return messages[messages.length - 1];
  }

  static getFullname(message: Message, participants: Participant[]): string {
    if (!participants) {
      return '';
    }
    const participant: Participant = this.getOwnerParticipant(message, participants);
    if (!participant) {
      return '';
    }
    return (
      participant.fullName ||
      `${participant.firstName} ${participant.lastName}`.trim() ||
      participant.email
    );
  }

  static getThumbUrl(message: Message, participants: Participant[]): string {
    const participant: Participant = this.getOwnerParticipant(message, participants);
    return participant?.thumbUrl;
  }

  static getParticipant(participants: Participant[], participantId: string) {
    return participants.find(({ id }) => id === participantId);
  }

  static getOwnerParticipant(message: Message, participants: Participant[]): Participant {
    return participants?.find(
      participant => message?.ownerId && participant.id === message.ownerId
    );
  }

  static getReplyMessage(message: Message, messages: Message[]): Message {
    if (!message.metadata?.repliedTo) {
      return undefined;
    }
    return messages.find(({ id }: Message) => id === message.metadata.repliedTo);
  }

  static hasMessagesForDate(messages: Message[], date: number) {
    const messagesForDate = messages.filter(message =>
      DateHelper.isSameDay(DateTime.fromMillis(message.createdAt), DateTime.fromMillis(date))
    );

    return messagesForDate.some(message => !message.deletedAt);
  }

  static showTimestamp(messages: Message[], previousMessage: Message, date: number) {
    if (!previousMessage) {
      return true;
    }

    if (!this.hasMessagesForDate(messages, date)) {
      return false;
    }

    return !DateHelper.isSameDay(
      DateTime.fromMillis(date),
      DateTime.fromMillis(previousMessage.createdAt)
    );
  }

  static date(date: number): string {
    return DateHelper.formattedDate(DateTime.fromMillis(date));
  }

  static newDate(date: number, locale?: string) {
    const userLocale = locale ? locale : navigator.language || 'en-US';
    const inputDate = DateTime.fromMillis(date, { locale: userLocale });
    const todayPlusMinusOne = DateHelper.todayPlusMinusOne(inputDate);

    return todayPlusMinusOne
      ? todayPlusMinusOne
      : inputDate.toFormat('EEEE, d LLLL', { locale: userLocale });
  }

  static time(date: number) {
    return DateTime.fromMillis(date).toLocaleString({ ...DateTime.TIME_SIMPLE });
  }

  static fullTimestamp(date: number) {
    return `${this.date(date)} ${t('at')} ${this.time(date)}`;
  }

  static isSameParticipant(firstParticipant: ChatParticipant, secondParticipantId: string) {
    return (
      secondParticipantId === firstParticipant.participantId ||
      secondParticipantId === firstParticipant.userId
    );
  }

  static getParticipantById = (
    participants: ChatParticipant[],
    participant: ChatParticipant
  ): ChatParticipant | undefined =>
    participants.find(
      ({ participantId, userId }) =>
        (participant.participantId && participant.participantId === participantId) ||
        (participant.userId && participant.userId === userId)
    );

  static getConversationParticipants = ({
    conversationParticipantIds,
    allParticipants,
  }: {
    conversationParticipantIds?: string[];
    allParticipants: Participant[];
  }): ChatParticipant[] =>
    conversationParticipantIds?.map(participantId => {
      const participant = allParticipants.find(p => p.id === participantId);

      return {
        participantId,
        participant: participant || { id: undefined, fullName: t('dr_chat_removed_participant') },
      };
    }) || [];

  static getParticipantsDetails = (chatParticipant: ChatParticipant) => {
    if (chatParticipant.participant) {
      return {
        id: chatParticipant.participant.id,
        fullName: chatParticipant.participant.fullName,
        thumbUrl: chatParticipant.participant.thumbUrl,
        title: chatParticipant.participant.title,
        email: chatParticipant.participant.email,
        deletedAt: chatParticipant.participant.deletedAt,
      };
    }
    return {
      id: chatParticipant.user.id,
      fullName: chatParticipant.user.fullName,
      thumbUrl: chatParticipant.user.thumbUrl,
      title: chatParticipant.user.title,
      email: chatParticipant.user.email,
      deletedAt: chatParticipant.user.deletedAt,
    };
  };

  static orderConversations = (conversations: Conversation[]) => {
    if (!conversations) {
      return [];
    }

    return conversations.sort((a, b) => {
      if (a.type === ConversationType.General) {
        return -1;
      }
      if (b.type === ConversationType.General) {
        return 1;
      }

      const lastMessageA =
        a.messages && a.messages.length > 0 ? a.messages[a.messages.length - 1] : null;
      const lastMessageB =
        b.messages && b.messages.length > 0 ? b.messages[b.messages.length - 1] : null;

      return (lastMessageB?.createdAt || 0) - (lastMessageA?.createdAt || 0);
    });
  };

  static getGroupConversations = (conversations: Conversation[], currentUserId?: string) => {
    if (!conversations) {
      return [];
    }

    return conversations.filter(conversation => {
      const isGeneralConversation = conversation.type === ConversationType.General;
      const noCurrentUserId = !currentUserId;
      const isParticipant = conversation.participantIds.includes(currentUserId);
      const moreThanTwoParticipants = conversation.participantIds.length > 2;

      return (
        ((noCurrentUserId || isParticipant) && moreThanTwoParticipants) || isGeneralConversation
      );
    });
  };

  static getPrivateConversations = (
    conversations: Conversation[],
    currentParticipantId: string,
    currentUserId: string
  ) => {
    if (!conversations) {
      return [];
    }

    return conversations.filter(conversation => {
      const isParticipant = ChatHelper.isParticipantInConversation(
        conversation,
        currentUserId,
        currentParticipantId
      );
      const participantsCount = conversation?.participantIds?.length;

      return (
        isParticipant && participantsCount === 2 && conversation.type === ConversationType.Private
      );
    });
  };

  static getUnavailableConversations = (
    conversations: Conversation[],
    currentParticipantId: string,
    currentUserId: string,
    removedParticipantsIds: string[]
  ) =>
    conversations.filter(conversation => {
      const reason = ChatHelper.getUnavailableReason({
        conversation,
        currentParticipantId,
        currentUserId,
        removedParticipantsIds,
      });

      return reason === ChatUnavailableReason.NotAMember;
    });

  static getParticipantNames = ({
    allParticipants,
    conversationParticipantIds,
    currentParticipantId,
    currentUserId,
  }: {
    allParticipants: Participant[];
    conversationParticipantIds: string[];
    currentParticipantId?: string;
    currentUserId?: string;
  }) => {
    const names = [];

    conversationParticipantIds.forEach(participantId => {
      if (participantId === currentParticipantId || participantId === currentUserId) {
        return;
      }

      const participant = allParticipants.find(p => p.id === participantId);
      if (participant) {
        names.push(AvatarHelper.getDisplayName(participant));
      } else {
        names.push(
          names.length > 0
            ? t('dr_chat_removed_participant').toLowerCase()
            : t('dr_chat_removed_participant')
        );
      }
    });

    return names.join(', ');
  };

  static findAllRemovedParticipantsIds = ({
    conversations,
    participants,
  }: {
    conversations: Conversation[];
    participants: Participant[];
  }) => {
    const allConversationParticipantIds = conversations.flatMap(
      conversation => conversation.participantIds
    );

    return allConversationParticipantIds.filter(
      participantId => !participants.some(({ id, deletedAt }) => id === participantId && !deletedAt)
    );
  };

  static isLastMessageOwner = (latestMessage: Message, userId: string, participantId: string) =>
    latestMessage?.ownerId === participantId || latestMessage?.ownerId === userId;

  static scrollConversationIntoView = () => {
    document.getElementById('conversation-list')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  static isParticipantInConversation = (
    conversation: Conversation,
    userId: string,
    participantId?: string
  ) => {
    const isGeneralConversation = conversation.type === ConversationType.General;
    const isParticipant =
      conversation.participantIds.includes(participantId) ||
      conversation.participantIds.includes(userId);

    return isParticipant || isGeneralConversation;
  };

  static isAPrivateConversationAndAllOtherParticipantsAreRemoved = (
    conversation: Conversation,
    currentParticipantId: string,
    currentUserId: string,
    removedParticipantsIds: string[]
  ) => {
    if (conversation.type !== ConversationType.Private) return false;

    return conversation.participantIds
      .filter(
        participantId => participantId !== currentParticipantId && participantId !== currentUserId
      )
      .every(participantId => removedParticipantsIds.includes(participantId));
  };

  static getUnavailableReason = ({
    conversation,
    currentParticipantId = '',
    currentUserId = '',
    removedParticipantsIds,
    canEdit = true,
  }: {
    conversation: Conversation;
    currentParticipantId?: string;
    currentUserId?: string;
    removedParticipantsIds: string[];
    canEdit?: boolean;
  }) => {
    if (
      !ChatHelper.isParticipantInConversation(conversation, currentUserId, currentParticipantId) ||
      !canEdit
    ) {
      return ChatUnavailableReason.NotAMember;
    }

    if (
      ChatHelper.isAPrivateConversationAndAllOtherParticipantsAreRemoved(
        conversation,
        currentParticipantId,
        currentUserId,
        removedParticipantsIds
      )
    ) {
      return ChatUnavailableReason.RemovedParticipant;
    }

    return null;
  };

  static isLastMessageUnread = (
    conversation: Conversation,
    userId: string,
    participantId: string
  ) => {
    if (!userId && !participantId) {
      return false;
    }

    const latestMessage = ChatHelper.getLastMessage(conversation.messages);
    const isParticipantInConversation = ChatHelper.isParticipantInConversation(
      conversation,
      userId,
      participantId
    );

    if (!latestMessage || !latestMessage.reads || !isParticipantInConversation) {
      return false;
    }

    if (ChatHelper.isLastMessageOwner(latestMessage, userId, participantId)) {
      return false;
    }
    return !latestMessage.reads.some(read =>
      participantId ? read?.participantId === participantId : read?.participantId === userId
    );
  };

  static findParticipantsInConversation = (
    conversation: Conversation,
    firstParticipantId: string,
    secondParticipantId: string
  ) =>
    conversation.participantIds.filter(
      participantId => participantId === firstParticipantId || participantId === secondParticipantId
    );

  static getConversationId = (
    participantId: string,
    currentParticipantId: string,
    conversations: Conversation[]
  ): OpenConversationPayload => {
    const conversation = conversations?.find(conversation => {
      const participantCount = conversation?.participantIds?.length;

      return (
        participantCount === 2 &&
        ChatHelper.findParticipantsInConversation(conversation, participantId, currentParticipantId)
          .length === 2
      );
    });

    return {
      conversationId: conversation?.id ?? null,
      participantId: conversation?.id ? null : participantId,
    };
  };

  static getMessageState(
    message: Message,
    participants: Participant[],
    messages: Message[],
    currentChatParticipant: ChatParticipant,
    locale?: string
  ) {
    const name = ChatHelper.getFullname(message, participants);
    const thumbUrl = ChatHelper.getThumbUrl(message, participants);
    const date = ChatHelper.newDate(message.createdAt, locale);
    const time = ChatHelper.time(message.createdAt);
    const fullTimestamp = ChatHelper.fullTimestamp(message.createdAt);
    const isBySender = ChatHelper.isSameParticipant(currentChatParticipant, message.ownerId);
    const replyMessage = ChatHelper.getReplyMessage(message, messages);

    return {
      name,
      thumbUrl,
      date,
      time,
      fullTimestamp,
      isBySender,
      replyMessage,
    };
  }

  static findLastRead = (messages: Message[], participantId?: string, userId?: string) => {
    for (const message of [...messages].reverse()) {
      const readBy = message.reads.find(({ participantId: id }) => {
        if (participantId && id) {
          return id === participantId;
        }

        return id === userId;
      });

      if (readBy) {
        return {
          messageId: message.id,
          readBy,
        };
      }
    }
  };

  static getExistingPrivateConversationId = (
    conversations: Conversation[],
    newParticipants: ChatParticipantId[]
  ) =>
    conversations.filter(
      conversation =>
        conversation.participantIds.length === newParticipants.length &&
        conversation.participantIds.every(participantId =>
          newParticipants.some(newParticipant =>
            ChatHelper.isSameParticipant(newParticipant, participantId)
          )
        ) &&
        conversation.type === ConversationType.Private
    )[0]?.id;

  static mapChatParticipant = (
    participantId: string,
    participants: Participant[],
    senders: Participant[]
  ) => ({
    participant: participants.find(({ id }) => id === participantId),
    user: senders?.find(({ id }) => id === participantId),
    participantId,
  });

  static findLastReadList = (
    conversation: Conversation,
    participants: Participant[],
    senders: Participant[]
  ) => {
    if (!conversation) {
      return [];
    }
    if (conversation.type === ConversationType.Private) {
      return conversation.participantIds.map(participantId =>
        ChatHelper.findLastRead(conversation.messages, participantId)
      );
    }

    const participantLastReadBy = participants.map(participant =>
      ChatHelper.findLastRead(conversation.messages, participant.id)
    );

    const userLastReadBy = senders.map(sender =>
      ChatHelper.findLastRead(conversation.messages, undefined, sender.id)
    );

    return [...participantLastReadBy, ...userLastReadBy];
  };

  static formatNewParticipants = (participants: ChatParticipant[]): ChatParticipantId[] =>
    participants.map(({ participant, participantId, userId }) =>
      participant ? { participantId } : { userId }
    );

  static mapMessage = (event: { conversation: Partial<Conversation>; message }): Message => ({
    ...event.message,
    ownerId: event.message.ownerId,
    reads: event.message.reads.map(read => ({
      ...read,
      participantId: read.participantId,
    })),
  });

  static findConversation = (conversations: Conversation[], conversationId: string) =>
    conversations?.find(conversation => conversation.id === conversationId);

  static mapConversations = (conversations: Conversation[], updatedConversation: Conversation) =>
    conversations.map(conversation =>
      conversation.id === updatedConversation.id ? updatedConversation : conversation
    );

  static getConversationLastRead = (
    conversation: Conversation,
    participants: Participant[],
    senders: Participant[],
    currentChatParticipant: ChatParticipant
  ) => {
    const reads = ChatHelper.findLastReadList(conversation, participants, senders).filter(
      read => !!read
    );

    return reads
      .filter(
        ({ readBy: { participantId } }) =>
          !ChatHelper.isSameParticipant(currentChatParticipant, participantId)
      )
      .map(({ readBy, messageId }) => ({
        messageId,
        readBy: {
          participant: ChatHelper.mapChatParticipant(readBy.participantId, participants, senders),
          participantId: readBy.participantId,
          timestamp: readBy.timestamp,
        },
      }));
  };

  static updateConversation = (
    conversation: Conversation,
    newMessage: Message,
    id: string,
    replace?: boolean
  ) => {
    const messageExists = !!conversation.messages?.find(message => message.id === id);
    const newMessages = conversation.messages.map(message => {
      if (message.id === id && replace) {
        return newMessage;
      }

      return message;
    });

    return {
      ...conversation,
      messages: messageExists ? newMessages : [...newMessages, newMessage],
    };
  };

  static isEmptyText = (text: string) => !text || text.trim() === '';

  static updateDraftMessages = (drafts: MessageDraft[], conversationId: string, text: string) => {
    const draftExists = drafts.some(message => message.conversationId === conversationId);
    const newDraft = { conversationId, text };

    if (!draftExists) {
      return !ChatHelper.isEmptyText(text) && conversationId ? [...drafts, newDraft] : drafts;
    }

    return drafts
      .map(draft => (draft.conversationId === conversationId ? newDraft : draft))
      .filter(draft => !ChatHelper.isEmptyText(draft.text));
  };

  static getDraftMessage = (drafts: MessageDraft[], conversationId: string) => {
    const draft = drafts.find(message => message.conversationId === conversationId);
    return draft?.text || '';
  };
}
